import React, {
    forwardRef, useCallback, useEffect, useState,
} from 'react';
import { getClassNames } from '../../utils';

import './RadioButton.css';
import uid from '../../utils/uid';
import { RADIO_SIZE_DEFAULT, RADIO_SIZES } from './constants';
import withTheme from '../../hocs/withTheme';

type RadioButtonSize = (typeof RADIO_SIZES)[number];

type RadioButtonProps = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'size' | 'onChange'
> & {
    error?: boolean | string;
    label: React.ReactNode;
    size?: RadioButtonSize;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    value?: string;
    id?: string | null;
    className?: string | null;
    innerRef?: React.Ref<HTMLInputElement>;
};

function RadioButton(props: RadioButtonProps) {
    const {
        error = false,
        label,
        size = RADIO_SIZE_DEFAULT,
        description,
        checked = false,
        defaultChecked = false,
        onChange = () => {},
        disabled = false,
        value = '',
        id = null,
        className = null,
        innerRef: ref = null,
        ...others
    } = props;

    const [inputValue, setInputValue] = useState(value);
    const [inputChecked, setInputChecked] = useState(checked || defaultChecked);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        setInputChecked(checked);
    }, [checked]);

    const classNameRadioButton = getClassNames(
        'radio-button',
        { 'radio-disabled': disabled },
        className,
    );

    const onChangeHandlar = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        if (typeof onChange === 'function') {
            onChange(event);
        }
        setInputChecked(event.target.value === inputValue);
    }, [onChange, setInputValue, inputValue, setInputChecked]);

    const uIdRadio = id || uid('radio');

    return (
        <div className={classNameRadioButton}>
            <input
                {...others}
                ref={ref}
                type="radio"
                className="radio-button-input"
                onChange={onChangeHandlar}
                value={inputValue}
                checked={inputChecked}
                disabled={disabled}
                id={uIdRadio}
            />
            <div className="radio-button-inner">
                <label className="radio-button-label" htmlFor={uIdRadio}>
                    {label}
                </label>
                {description && <span className="radio-button-description">{description}</span>}
            </div>
        </div>
    );
}


export default forwardRef<HTMLInputElement, RadioButtonProps>((props, ref) =>
    withTheme(RadioButton)({ ...props, innerRef: ref }),
);
